import { useEffect, useState } from "react";
import { Button, Form, Input, InputNumber, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { handleAllowNumbersOnly } from "../../helpers/helpfulFunctions";
import { requestWithdrawalFromWallet } from "../../network";
import { toastError, toastSuccess } from "../../helpers/toasters";
import closeIcon from "../../assets/icons/buy-and-supply-close-button.svg";
import CashWithdrawalIcon from "./Icons/CashWithdrawalIcon";
import BankTransferIcon from "./Icons/BankTransferIcon";

const STEP = {
  METHOD: "method",
  CASH_CONFIRM: "cashConfirm",
  BANK_FORM: "bankForm",
};

const GULF_CASH_COLLECTION = {
  CASH: 1,
  BANK: 2,
};

export default function WithdrawalModal({
  open,
  onCancel,
  balance,
  currency,
  onSuccess,
}) {
  const { t } = useTranslation();
  const { dir } = useSelector((state) => state.language);
  const [step, setStep] = useState(STEP.METHOD);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState(null);
  const [amountError, setAmountError] = useState("");
  const [form] = Form.useForm();
  const maxBalance = Number(balance) || 0;

  useEffect(() => {
    if (open) {
      setStep(STEP.METHOD);
      setWithdrawalAmount(null);
      setAmountError("");
      form.resetFields();
      setIsSubmitting(false);
    }
  }, [open, form]);

  const validateAmount = (value = withdrawalAmount) => {
    const numericValue = Number(value);
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      Number.isNaN(numericValue) ||
      numericValue <= 0
    ) {
      setAmountError(t("buyAndSupply2026.requiredWithdrawalAmount"));
      return false;
    }
    if (numericValue > maxBalance) {
      setAmountError(t("buyAndSupply2026.exceedingWithdrawalBalance"));
      return false;
    }
    setAmountError("");
    return true;
  };

  const handleSelectMethod = (nextStep) => {
    if (!validateAmount()) return;
    setStep(nextStep);
  };

  const handleAmountChange = (value) => {
    setWithdrawalAmount(value);
    const numericValue = Number(value);
    if (
      value !== null &&
      value !== undefined &&
      !Number.isNaN(numericValue) &&
      numericValue > maxBalance
    ) {
      setAmountError(t("buyAndSupply2026.exceedingWithdrawalBalance"));
      return;
    }
    setAmountError("");
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setStep(STEP.METHOD);
    setWithdrawalAmount(null);
    setAmountError("");
    form.resetFields();
    onCancel();
  };

  const submitWithdrawal = (payload) => {
    setIsSubmitting(true);
    requestWithdrawalFromWallet(
      payload,
      (res) => {
        setIsSubmitting(false);
        if (res.success) {
          toastSuccess(res.message);
          setStep(STEP.METHOD);
          setWithdrawalAmount(null);
          setAmountError("");
          form.resetFields();
          onSuccess?.();
          onCancel();
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        setIsSubmitting(false);
        toastError(res?.message);
      },
    );
  };

  const handleCashConfirm = () => {
    if (!validateAmount()) {
      setStep(STEP.METHOD);
      return;
    }
    submitWithdrawal({
      amount: Number(withdrawalAmount),
      gulfCashCollectionTypeId: GULF_CASH_COLLECTION.CASH,
    });
  };

  const handleBankSubmit = (values) => {
    if (!validateAmount()) {
      setStep(STEP.METHOD);
      return;
    }
    submitWithdrawal({
      amount: Number(withdrawalAmount),
      gulfCashCollectionTypeId: GULF_CASH_COLLECTION.BANK,
      bankDetails: {
        bankName: values.bankName,
        accountName: values.accountName,
        accountNumberOrIBAN: values.accountNumberOrIBAN,
      },
    });
  };

  const modalWidth =
    step === STEP.BANK_FORM
      ? window.innerWidth >= 768
        ? 480
        : "100%"
      : step === STEP.CASH_CONFIRM
        ? window.innerWidth >= 768
          ? 420
          : "90%"
        : window.innerWidth >= 768
          ? 520
          : "100%";

  const showClose = step !== STEP.CASH_CONFIRM;

  return (
    <Modal
      centered
      destroyOnClose
      open={open}
      footer={false}
      closable={showClose}
      maskClosable={false}
      closeIcon={<img src={closeIcon} alt="" className="d-block img-fluid" />}
      width={modalWidth}
      className={`buy-supply-2026-withdrawal-modal buy-supply-2026-withdrawal-modal--${step}`}
      onCancel={handleClose}
    >
      <div dir={dir} className="buy-supply-2026-withdrawal-modal__body">
        {step === STEP.METHOD && (
          <>
            <h2 className="buy-supply-2026-withdrawal-modal__title">
              {t("buyAndSupply2026.requestWithdrawal")}
            </h2>
            <label
              className="buy-supply-2026-withdrawal-modal__balance-label"
              htmlFor="withdrawal-amount"
            >
              {t("buyAndSupply2026.balance")}
            </label>
            <InputNumber
              id="withdrawal-amount"
              className="buy-supply-2026-withdrawal-modal__balance-input"
              value={withdrawalAmount}
              min={0}
              controls={false}
              addonAfter={currency}
              placeholder={`${maxBalance}`}
              onKeyDown={(e) => handleAllowNumbersOnly(e, true)}
              onChange={handleAmountChange}
              status={amountError ? "error" : undefined}
            />
            {amountError ? (
              <p className="buy-supply-2026-withdrawal-modal__balance-error">
                {amountError}
              </p>
            ) : null}
            <div className="buy-supply-2026-withdrawal-modal__methods">
              <button
                type="button"
                className="buy-supply-2026-withdrawal-modal__method"
                onClick={() => handleSelectMethod(STEP.CASH_CONFIRM)}
              >
                <CashWithdrawalIcon />
                <span>{t("buyAndSupply2026.cashWithdrawal")}</span>
              </button>
              <button
                type="button"
                className="buy-supply-2026-withdrawal-modal__method"
                onClick={() => handleSelectMethod(STEP.BANK_FORM)}
              >
                <BankTransferIcon />
                <span>{t("buyAndSupply2026.bankTransfer")}</span>
              </button>
            </div>
          </>
        )}

        {step === STEP.CASH_CONFIRM && (
          <div className="buy-supply-2026-withdrawal-modal__confirm">
            <p className="buy-supply-2026-withdrawal-modal__confirm-title">
              {t("buyAndSupply2026.cashConfirmTitle")}
            </p>
            <Button
              type="primary"
              loading={isSubmitting}
              className="buy-supply-2026-withdrawal-modal__primary-btn"
              onClick={handleCashConfirm}
            >
              {t("buyAndSupply2026.yes")}
            </Button>
            <button
              type="button"
              className="buy-supply-2026-withdrawal-modal__cancel-link"
              disabled={isSubmitting}
              onClick={() => setStep(STEP.METHOD)}
            >
              {t("buyAndSupply2026.cancel")}
            </button>
          </div>
        )}

        {step === STEP.BANK_FORM && (
          <>
            <h2 className="buy-supply-2026-withdrawal-modal__title">
              {t("buyAndSupply2026.bankDetailsTitle")}
            </h2>
            <Form
              form={form}
              layout="vertical"
              className="buy-supply-2026-withdrawal-modal__form"
              onFinish={handleBankSubmit}
              requiredMark={false}
            >
              <Form.Item
                name="bankName"
                label={t("buyAndSupply2026.bankName")}
                rules={[
                  {
                    required: true,
                    message: t("buyAndSupply2026.requiredBankName"),
                  },
                ]}
              >
                <Input
                  placeholder={t("buyAndSupply2026.bankNamePlaceholder")}
                />
              </Form.Item>
              <Form.Item
                name="accountName"
                label={t("buyAndSupply2026.accountName")}
                rules={[
                  {
                    required: true,
                    message: t("buyAndSupply2026.requiredAccountName"),
                  },
                ]}
              >
                <Input
                  placeholder={t("buyAndSupply2026.accountNamePlaceholder")}
                />
              </Form.Item>
              <Form.Item
                name="accountNumberOrIBAN"
                label={t("buyAndSupply2026.accountNumberOrIban")}
                rules={[
                  {
                    required: true,
                    message: t("buyAndSupply2026.requiredAccountNumber"),
                  },
                ]}
              >
                <Input
                  placeholder={t(
                    "buyAndSupply2026.accountNumberOrIbanPlaceholder",
                  )}
                />
              </Form.Item>
              {/* <Form.Item
                name="gulfCountryId"
                label={t("buyAndSupply2026.country")}
                rules={[
                  {
                    required: true,
                    message: t("buyAndSupply2026.requiredCountry"),
                  },
                ]}
              >
                <InputNumber
                  className="w-100"
                  placeholder={t("buyAndSupply2026.countryPlaceholder")}
                  controls={false}
                />
              </Form.Item> */}
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="buy-supply-2026-withdrawal-modal__primary-btn"
              >
                {t("buyAndSupply2026.submit")}
              </Button>
              <button
                type="button"
                className="buy-supply-2026-withdrawal-modal__cancel-link"
                disabled={isSubmitting}
                onClick={() => {
                  form.resetFields();
                  setStep(STEP.METHOD);
                }}
              >
                {t("buyAndSupply2026.cancel")}
              </button>
            </Form>
          </>
        )}
      </div>
    </Modal>
  );
}
